import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { MessageThread } from "../models/MessageThread";
import { selectThreadMessages } from "../selectors/messages-selectors";

export interface DirectThreadInPlace {
  /** Below `lg` the thread column is shown in place of the list, without a picked thread in the URL. */
  open: boolean;
  /** The member's only thread is the Direct one and nobody has written in it yet. */
  awaitingFirstMessage: boolean;
}

/**
 * The portal's first-message case (US-92): when the member's only thread is their Direct thread with
 * no message yet, the phone shows that thread instead of a list of one row. It stays open after the
 * first send until the member leaves through the back link (a new location) or switches member.
 */
export const useDirectThreadInPlace = (threads: MessageThread[], threadId?: string): DirectThreadInPlace => {
  const location = useLocation();
  const sole = !threadId && threads.length === 1 && threads[0].kind === "Direct" ? threads[0] : null;
  const loaded = useAppSelector(selectThreadMessages(sole?.id ?? ""));
  const awaitingFirstMessage = Boolean(sole && sole.lastMessageAt === null && (loaded?.items.length ?? 0) === 0);
  const [openedAt, setOpenedAt] = useState<string | null>(null);

  useEffect(() => {
    if (awaitingFirstMessage) setOpenedAt(location.key);
  }, [awaitingFirstMessage, location.key]);

  const open = Boolean(sole) && (awaitingFirstMessage || openedAt === location.key);
  return { open, awaitingFirstMessage };
};
